/**
 * A song's parts as buildChartRun entries, straight off the song's data — for playing it through
 * where the parts aren't on the page to hand their charts in (see ChartRunContext), as in the song
 * form.
 */

import { carryPadModes, toIntensityEntry } from './chartIntensity';
import { getSectionStartBars } from './chordBars';
import { BLOCK_CHORDS, getChartRhythm, toRhythmId } from './chordRhythmPatterns';
import { getChartInstrumentId, getPartInstrumentId } from './midiInstruments';
import { getMidiTranspositionToKey, hasMidi } from './midiJson';
import { getPartGrid } from './partCharts';
import { getPartKeys } from './partKey';
import { getPartBpm } from './partTempo';
import { isSectionPart } from './partNumbers';
import { getMelodySection, isDrumSolo } from './parts';
import { getBeatsPerBar, getRepeatCount } from './songTiming';

/**
 * The part's MIDI under its chart, in the key the part is in, as the song page plays it. A clip
 * tagged for an instrument starts at that instrument's section, and one whose instrument has no
 * section in the chart is left out.
 */
const getPartMelody = (part, grid, key, sourceKey) => {
  if (!hasMidi(part)) return null;

  const sectionCount = (grid.sections?.[grid.sections.length - 1] ?? 0) + 1;
  const instruments =
    sectionCount > 1 ? Array.from({ length: sectionCount }, (unused, i) => part.assignedInstruments?.[i]) : [];
  const { plays, section } = getMelodySection(part, instruments);

  if (!plays) return null;

  return {
    midiJson: part.midi,
    instrumentId: getPartInstrumentId(part.midi),
    semitones: getMidiTranspositionToKey(part.midi, key, sourceKey),
    atBar: section > 0 ? getSectionStartBars(grid.lines, grid.sections)[section] || 0 : 0,
  };
};

/**
 * @param {object[]} parts - The song's parts, in order
 * @param {object} song - `{ bpm, timeSignature, chartInstrumentId, chartRhythm, key, sourceKey }`, `key`
 *   as the form now says and `sourceKey` as the song was loaded, to put each part's MIDI in its key
 * @param {function} canPlay - Whether a part plays through
 * @returns {object[]} One per part with a chart, tagged with its `partId`, the pad's setting carried
 *   to it from the parts before (carryPadModes)
 */
export const getSongRunEntries = (parts = [], song = {}, canPlay = () => true) => {
  const beatsPerBar = getBeatsPerBar(song.timeSignature);
  const partKeys = getPartKeys(parts, song.key);

  const entries = parts.flatMap((part) => {
    // Kept only to start the pad afresh, and dropped below
    if (isSectionPart(part)) return [{ section: true }];

    const grid = canPlay(part) ? getPartGrid(part, beatsPerBar) : null;

    if (!grid) return [];

    // A duplicate plays at its original's tempo
    const original = part.isDuplicate ? parts.find((other) => other.id === part.duplicateId) || part : part;

    return [
      {
        partId: part.id,
        name: part.name,
        lines: grid.lines,
        countInEntry: Boolean(grid.countInEntry),
        bpm: getPartBpm(original, song.bpm) || Number(song.bpm),
        instrumentId: getChartInstrumentId(part, song),
        rhythm: toRhythmId(getChartRhythm(part, song) || BLOCK_CHORDS, beatsPerBar),
        repeatCount: getRepeatCount(part),
        ...toIntensityEntry(part),
        drumsOnly: isDrumSolo(part),
        melody: getPartMelody(part, grid, partKeys[part.id]?.key || song.key, song.sourceKey),
      },
    ];
  });

  return carryPadModes(entries).filter((entry) => !entry.section);
};
